import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPieSimpleCircleDollar } from "@fortawesome/pro-regular-svg-icons";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { getRandomColor } from "~/utils/getRandomColor";
import {
    DEFAULT_PIE_CHART_DATA_LABEL_PROPS,
    PieChart,
} from "~/modules/visualization/components/PieChart";
import {
    DistributionSelect,
    getDistributionOptions,
} from "~/components/DistributionSelect";
import { ColumnSpanConfig, DistributionType } from "~/types";
import { DistributionTypes } from "~/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import * as Analytics from "~/modules/league_tables/analytics";
import { SelectInput } from "~/components/SelectInput";
import { ToggleRow } from "~/modules/visualization/components/Timeseries/ToggleRow";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { ButtonGroup } from "~/components/ButtonGroup";

// // // //

const GROUP_BY_LABELS: Partial<Record<LeagueTableGroupTypes, string>> = {
    [LeagueTableGroupTypes.assetClasses]: "Asset Class",
    [LeagueTableGroupTypes.networks]: "Network",
    [LeagueTableGroupTypes.parentNetworks]: "Parent Network",
    [LeagueTableGroupTypes.platforms]: "Platform",
    [LeagueTableGroupTypes.issuers]: "Issuer",
    [LeagueTableGroupTypes.jurisdictionCountries]: "Jurisdiction",
    [LeagueTableGroupTypes.managers]: "Manager",
    [LeagueTableGroupTypes.peggedAssets]: "Pegged Asset",
    [LeagueTableGroupTypes.realEstateCountries]: "Real Estate Country",
    [LeagueTableGroupTypes.assets]: "Asset",
    [LeagueTableGroupTypes.tokens]: "Token",
};

/**
 * Whether any tab under any distribution type has rows to chart
 */
function hasAnyRows(tabs: LeagueTableTabsByDistributionType): boolean {
    return [...tabs.all, ...tabs.distributed, ...tabs.represented].some(
        (t) => t.data.rows.length > 0,
    );
}

export function LeagueTablePieChart(props: {
    title?: string;
    colSpans?: ColumnSpanConfig;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    /**
     * The same league table data with stablecoins excluded. Passing this
     * enables the "Include Stablecoins" toggle: the chart renders
     * `leagueTableTabs` while the toggle is on and this while it's off.
     */
    leagueTableTabsWithoutStablecoins?: LeagueTableTabsByDistributionType;
    defaultGroupBy?: LeagueTableGroupTypes;
    allowedGroupByValues?: LeagueTableGroupTypes[];
    groupByLabelOverrides?: Partial<Record<LeagueTableGroupTypes, string>>;
    onChangeGroupBy?: (obj: {
        prevValue: LeagueTableGroupTypes;
        nextValue: LeagueTableGroupTypes;
    }) => void;
    onChangeIncludeStablecoins?: (obj: { includeStablecoins: boolean }) => void;
}) {
    const {
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6,
            xxl: 6,
        },
        defaultGroupBy = LeagueTableGroupTypes.assetClasses,
        title = "Total RWA Value",
        allowedGroupByValues = [],
        groupByLabelOverrides,
        onChangeGroupBy,
        onChangeIncludeStablecoins,
    } = props;

    // Determine if there is distributed or represented data
    const hasDistributedData = leagueTableTabs.distributed.some(
        (t) => t.data.rows.length > 0,
    );
    const hasRepresentedData = leagueTableTabs.represented.some(
        (t) => t.data.rows.length > 0,
    );

    // Determine the initial transferable filter based on the data
    const initialTransferableFilter =
        hasDistributedData && hasRepresentedData
            ? DistributionTypes.Distributed
            : DistributionTypes.All;

    const [transferableFilter, setTransferableFilter] =
        useState<DistributionType>(initialTransferableFilter);

    const [groupBy, setGroupBy] =
        useState<LeagueTableGroupTypes>(defaultGroupBy);

    // Stablecoins state — the toggle is only available when the caller
    // supplies the stablecoin-free dataset. It opens off, unless excluding
    // stablecoins would leave nothing to chart (e.g. a platform that only
    // tokenizes stablecoins), in which case it opens on.
    const stablecoinsToggleEnabled =
        leagueTableTabsWithoutStablecoins !== undefined;

    const [includeStablecoins, setIncludeStablecoins] = useState(
        leagueTableTabsWithoutStablecoins
            ? !hasAnyRows(leagueTableTabsWithoutStablecoins)
            : false,
    );

    // Represented assets have no stablecoin breakdown, so the toggle reads as
    // off and does nothing in that mode — same rule as TimeseriesCard.
    const stablecoinsDisabled =
        transferableFilter === DistributionTypes.Represented;
    const stablecoinsChecked = includeStablecoins && !stablecoinsDisabled;

    const activeLeagueTableTabs =
        leagueTableTabsWithoutStablecoins && !stablecoinsChecked
            ? leagueTableTabsWithoutStablecoins
            : leagueTableTabs;

    const distributedLeagueTableTabs = activeLeagueTableTabs.distributed.find(
        (t) => t.data.groupType === groupBy,
    );
    const representedLeagueTableTabs = activeLeagueTableTabs.represented.find(
        (t) => t.data.groupType === groupBy,
    );
    const allLeagueTableTabs = activeLeagueTableTabs.all.find(
        (t) => t.data.groupType === groupBy,
    );

    const chartData = {
        [DistributionTypes.Distributed]:
            distributedLeagueTableTabs?.data.rows || [],
        [DistributionTypes.Represented]:
            representedLeagueTableTabs?.data.rows || [],
        [DistributionTypes.All]: allLeagueTableTabs?.data.rows || [],
    };

    const filteredChartData = chartData[transferableFilter];

    // The distribution select hides itself below three options, so only
    // render its wrappers when both distribution types have data.
    const showDistributionSelect = hasDistributedData && hasRepresentedData;

    const changeDistribution = (value: DistributionType) => {
        setTransferableFilter(value);

        Analytics.trackChangeDistribution({
            distributionType: value,
        });
    };

    const selectElement =
        allowedGroupByValues.length > 0 ? (
            <div className="flex items-center gap-2">
                <p className="text-sm text-foreground whitespace-nowrap">
                    Group By
                </p>
                <SelectInput
                    value={groupBy}
                    size="sm"
                    onChange={(updatedValue) => {
                        if (onChangeGroupBy) {
                            onChangeGroupBy({
                                prevValue: groupBy,
                                nextValue:
                                    updatedValue as LeagueTableGroupTypes,
                            });
                        }
                        setGroupBy(updatedValue as LeagueTableGroupTypes);
                    }}
                    options={allowedGroupByValues.map((g) => ({
                        value: g,
                        content: {
                            label:
                                groupByLabelOverrides?.[g] ??
                                GROUP_BY_LABELS[g] ??
                                g,
                        },
                    }))}
                />
            </div>
        ) : null;

    const stablecoinsToggleElement = stablecoinsToggleEnabled ? (
        // On phones this stretches with the switch at the right edge. From
        // `md` up the control bar is row-reversed so `mr-auto` pins it to the
        // left edge, opposite the group-by select.
        <div className="md:mr-auto">
            <ToggleRow
                label="Include Stablecoins"
                description="Stablecoins, cash and cash-equivalents"
                checked={stablecoinsChecked}
                disabled={stablecoinsDisabled}
                onChange={(updatedValue) => {
                    if (onChangeIncludeStablecoins) {
                        onChangeIncludeStablecoins({
                            includeStablecoins: updatedValue,
                        });
                    }
                    setIncludeStablecoins(updatedValue);
                }}
            />
        </div>
    ) : null;

    // The distribution select, when shown, takes the header's upper-right
    // slot: a full-width "Asset Type" segmented control on phones, the tab
    // strip hugging its content beside the title from `md` up. Without it,
    // the group-by select takes that slot instead.
    const headerControls = showDistributionSelect ? (
        <div className="w-full md:w-auto">
            <div className="w-full md:hidden">
                <div className="flex w-full flex-col gap-1.5">
                    <span className="text-sm text-foreground select-none">
                        Asset Type
                    </span>
                    <ButtonGroup
                        size="sm"
                        fullWidth
                        options={getDistributionOptions({
                            distributedEnabled: hasDistributedData,
                            representedEnabled: hasRepresentedData,
                        })}
                        value={transferableFilter}
                        disabled={false}
                        onValueChange={(value) =>
                            changeDistribution(value as DistributionType)
                        }
                    />
                </div>
            </div>
            <div className="hidden md:block">
                <DistributionSelect
                    value={transferableFilter}
                    onChange={changeDistribution}
                    includeDescription={false}
                    includeTooltips={true}
                    label={null}
                    enabledOptions={{
                        distributed: hasDistributedData,
                        represented: hasRepresentedData,
                    }}
                />
            </div>
        </div>
    ) : (
        selectElement
    );

    // With the distribution select in the header, the group-by select joins
    // the stablecoins row in the control bar beneath. On phones the bar stacks
    // the group-by then the stablecoins row full-width. From `md` up they
    // share one row — the stablecoins row left-justified under the title, the
    // group-by right-aligned under the distribution select — reversed so the
    // phone stacking order stays group-by first without reordering the DOM.
    const groupBySelectInControlBar = showDistributionSelect
        ? selectElement
        : null;

    const controlBarElement =
        groupBySelectInControlBar || stablecoinsToggleElement ? (
            <div className="flex flex-col gap-3 mt-1 mb-2 md:mt-2 md:flex-row-reverse md:items-center">
                {groupBySelectInControlBar}
                {stablecoinsToggleElement}
            </div>
        ) : null;

    return (
        <VisualizationCard
            title={title}
            colSpans={colSpans}
            cardClassName="[&>div:first-child]:md:items-start"
            wrapperClassNameOverride=""
            customControls={headerControls}
            customControlBar={controlBarElement}
        >
            {filteredChartData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] gap-3 text-muted-foreground">
                    <FontAwesomeIcon
                        icon={faChartPieSimpleCircleDollar}
                        className="h-8 w-8"
                    />
                    <p className="text-sm font-light">
                        No data available for this selection
                    </p>
                </div>
            ) : (
                <WindowResizeObserver
                    delay={1200}
                    defaultPlaceholderHeight={330}
                >
                    <PieChart
                        plotOptions={{
                            dataLabels: {
                                ...DEFAULT_PIE_CHART_DATA_LABEL_PROPS,
                                enabled: true,
                                allowOverlap: true,
                                minSize: 0,
                                filter: {
                                    property: "percentage",
                                    operator: ">",
                                    value: 0,
                                },
                            },
                        }}
                        series={filteredChartData
                            .map((p) => {
                                return {
                                    id: p.group.id,
                                    name: p.group.name,
                                    value: p.value,
                                    color: p.group.color || getRandomColor(),
                                };
                            })
                            .filter((p) => {
                                return p.value > 1;
                            })}
                    />
                </WindowResizeObserver>
            )}
        </VisualizationCard>
    );
}
